<template>
  <div class="page-container">
    <el-card v-loading="loading">
      <!-- 元信息栏 -->
      <div v-if="current" class="agreement-meta">
        <el-descriptions :column="4" border size="small">
          <el-descriptions-item label="协议标题">{{ current.title }}</el-descriptions-item>
          <el-descriptions-item label="版本号">v{{ current.version }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="current.status === 1 ? 'success' : 'danger'" size="small">
              {{ current.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="最后更新">{{ current.updated_at }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 内容预览 -->
      <div v-if="!editing" class="preview-wrap">
        <div class="preview-toolbar">
          <span class="preview-label">{{ agreementLabel }}内容</span>
          <el-button
            v-permission="['system:agreement:update']"
            type="primary" size="mini" icon="el-icon-edit"
            @click="startEdit"
          >编辑</el-button>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="preview-body" v-html="current && current.content ? current.content : '<p class=\'empty-tip\'>暂无内容，点击右上角「编辑」添加</p>'" />
      </div>

      <!-- 编辑表单 -->
      <el-form v-else ref="editForm" :model="editForm" :rules="editRules" label-width="80px" class="edit-form">
        <el-row :gutter="24">
          <el-col :span="14">
            <el-form-item label="标题" prop="title">
              <el-input v-model="editForm.title" placeholder="请输入协议标题" />
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item label="版本号" prop="version">
              <el-input v-model="editForm.version" placeholder="如 1.0" />
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item label="状态">
              <el-radio-group v-model="editForm.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="0">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="内容" prop="content">
          <rich-editor v-model="editForm.content" :height="480" placeholder="请输入协议内容..." />
        </el-form-item>
        <el-form-item>
          <el-button size="mini" @click="cancelEdit">取消</el-button>
          <el-button type="primary" size="mini" :loading="saving" @click="handleSave">保存</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { getAgreementDetail, updateAgreement } from '@/api/agreement'

export default {
  name: 'AgreementManage',
  data() {
    return {
      loading: false,
      saving: false,
      editing: false,
      current: null,
      editForm: { title: '', content: '', version: '', status: 1 },
      editRules: {
        title:   [{ required: true, message: '标题不能为空', trigger: 'blur' }],
        version: [{ required: true, message: '版本号不能为空', trigger: 'blur' }],
        content: [{ required: true, message: '内容不能为空', trigger: 'blur' }]
      }
    }
  },
  computed: {
    // 从路由路径中推断协议类型
    agreementType() {
      const path = this.$route.path
      if (path.includes('partner-privacy') || path.includes('partner_privacy')) return 'partner_privacy_policy'
      if (path.includes('partner')) return 'partner_user_agreement'
      if (path.includes('privacy')) return 'privacy_policy'
      return 'user_agreement'
    },
    agreementLabel() {
      const labelMap = {
        user_agreement: '用户协议',
        privacy_policy: '隐私协议',
        partner_user_agreement: '合伙人用户协议',
        partner_privacy_policy: '合伙人隐私协议'
      }
      return labelMap[this.agreementType] || '协议'
    }
  },
  watch: {
    // 切换菜单（路由变更）时重新加载
    $route() {
      this.editing = false
      this.fetchDetail()
    }
  },
  created() {
    this.fetchDetail()
  },
  methods: {
    async fetchDetail() {
      this.loading = true
      try {
        const res = await getAgreementDetail({ type: this.agreementType })
        this.current = res.data
      } finally {
        this.loading = false
      }
    },

    startEdit() {
      if (!this.current) return
      this.editForm = {
        title:   this.current.title,
        content: this.current.content || '',
        version: this.current.version,
        status:  this.current.status
      }
      this.editing = true
    },

    cancelEdit() {
      this.editing = false
      this.$refs.editForm && this.$refs.editForm.resetFields()
    },

    handleSave() {
      this.$refs.editForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const res = await updateAgreement({
            type:    this.agreementType,
            title:   this.editForm.title,
            content: this.editForm.content,
            version: this.editForm.version,
            status:  this.editForm.status
          })
          this.$message.success('保存成功')
          this.current = res.data
          this.editing = false
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.agreement-meta {
  margin-bottom: 20px;
}

.preview-wrap {
  .preview-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    .preview-label {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
    }
  }
  .preview-body {
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    padding: 24px 28px;
    min-height: 240px;
    background: #fafafa;
    line-height: 1.8;
    color: #303133;
    font-size: 14px;
    overflow: auto;

    ::v-deep .empty-tip { color: #c0c4cc; }
    ::v-deep h1, ::v-deep h2, ::v-deep h3 { margin: 14px 0 8px; font-weight: 600; }
    ::v-deep p  { margin: 0 0 10px; }
    ::v-deep ul, ::v-deep ol { padding-left: 20px; margin-bottom: 10px; }
    ::v-deep a  { color: #409eff; }
  }
}

.edit-form {
  margin-top: 8px;
  // 让富文本编辑器的 el-form-item 撑满宽度
  ::v-deep .el-form-item__content {
    line-height: normal;
  }
}
</style>
